import Card from "components/ui/Card";
import Filter from "components/ui/Filter";
import ModalWarning from "components/ui/ModalWarning";
import NavTab, { TabItem } from "components/ui/NavTab";
import Spinner from "components/ui/Spinner";
import StepNav from "components/ui/StepNav";
import StepTitle from "components/ui/StepTitle";
import { useAuth } from "context/AuthContext";
import { useModal } from "context/ModalContext/Modal.context";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  getCategories,
  getRealizations,
  getSubscribedRealizations,
  isSubscribedRealization,
} from "services/realization.service";
import {
  categories,
  Realization,
  RealizationSubscribed,
} from "types/realization";
import { StageIds } from "utils/constants";
import { clearRegistrationUser } from "utils/fns";

const Realizations = () => {
  const navigate = useNavigate();
  const { showModal } = useModal();

  const { user, updateNotifications, appCurrentStep, currentStep } = useAuth();
  const [loading, setLoading] = useState(false);

  const [filter, setFilter] = useState<{
    category?: string;
    searchTerm?: string;
  }>({});

  const [tabs, setTabs] = useState([
    {
      title: `Realizações disponives`,
      number: 0,
      filter: "SUBSCRIPTION",
    },
    {
      title: "Realizações já inscritas",
      number: 0,
      filter: "CONFIRM",
    },
  ]);

  const [currentTab, setCurrentTab] = useState<TabItem>(tabs[0]);

  const [realizations, setRealizations] = useState<Realization[]>([]);
  const [subscribedRealization, setSubscribedRealizations] = useState<
    RealizationSubscribed[]
  >([]);

  const [cardType, setCardType] = useState<"SUBSCRIPTION" | "CONFIRM">(
    "SUBSCRIPTION"
  );

  const [categories, setCategories] = useState<categories[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [firstLoaded, setFirstLoaded] = useState(false);
  const [isElegible, setIsElegible] = useState(false);
  const [isFilter, setIsFilter] = useState(false);

  useEffect(() => {
    async function fetchCategories() {
      try {
        const categoriesData = await getCategories();
        const formattedCategories = categoriesData.map((category: any) => ({
          _id: category._id,
          name: `${category._id}`,
        }));
        setCategories([
          { _id: "-1", name: "Filtrar por" },
          ...formattedCategories.filter(
            (category: any) =>
              category.name !== "N/A" && category.name.trim() !== ""
          ),
        ]);
      } catch (e) {
        console.log(e);
      }
    }
    fetchCategories();
  }, []);

  useEffect(() => {
    if (currentStep?._id !== StageIds.STAGE_INSCRICAO) {
      navigate("/");
    }
  }, [currentStep]);

  const fetchRealization = useCallback(async () => {
    if (!user?._id) return;

    try {
      setLoading(true);

      const [subscribed, data] = await Promise.all([
        getSubscribedRealizations(user),
        getRealizations(user),
      ]);

      const mappedSubscribedRealizations = new Set(
        subscribed.map((item: any) => item.id_realizacao)
      );

      const newItensRealizations = data.filter(
        (item: any) => !mappedSubscribedRealizations.has(item._id)
      );

      setRealizations(
        newItensRealizations.filter(
          (item: any) =>
            filter.category === "-1" ||
            !filter.category ||
            item.category === filter.category
        )
      );

      setSubscribedRealizations(
        subscribed.filter(
          (item: any) =>
            filter.category === "-1" ||
            !filter.category ||
            item.category === filter.category
        )
      );

      setIsLoaded(true);

      setTabs((prevTabs) =>
        prevTabs.map((tab) => ({
          ...tab,
          number:
            tab.filter === "SUBSCRIPTION"
              ? newItensRealizations.length
              : tab.filter === "CONFIRM"
              ? subscribed.length
              : tab.number,
        }))
      );

      if (!isFilter && data.length === 0 && subscribed.length === 0) {
        navigate("/winners");
      } else {
        setIsElegible(true);
      }
    } catch (e: any) {
      console.error(e);
    } finally {
      setFirstLoaded(true);
      setLoading(false);
      updateNotifications();
    }
  }, [user, appCurrentStep, filter, isFilter, navigate]);

  useEffect(() => {
    setIsFilter(true);
    fetchRealization();
  }, [fetchRealization]);

  useEffect(() => {
    setCardType(
      currentTab.filter === "SUBSCRIPTION" ? "SUBSCRIPTION" : "CONFIRM"
    );
  }, [currentTab]);

  const handlerConfirm = async (item: RealizationSubscribed) => {
    try {
      showModal(
        "subscribed",
        <ModalWarning
          title="A inscrição desta iniciativa já foi realizada por outro participante!"
          description={
            "Continue o processo para selecionar 5 participantes da realização cadastrada para participarem da premiação individual e confirmar sua participação no prêmio BB."
          }
          btnText={"Continuar"}
          onClose={() => {
            navigate("/realizations/subscribe", {
              state: { data: item, action: "indication" },
            });
          }}
        />,
        "is-subscribed"
      );
    } catch (e) {}
  };

  const handlerNext = async (item: Realization | RealizationSubscribed) => {
    try {
      if (user) {
        const currentPlayer = clearRegistrationUser(user?._id);
        const subscribed = await isSubscribedRealization(item._id, user?._id);

        if (!!subscribed.player) {
          showModal(
            "subscribed",
            <ModalWarning
              title="A inscrição desta iniciativa já foi realizada por outro participante!"
              description={
                "Continue o processo para selecionar 5 participantes da realização cadastrada para participarem da premiação individual e confirmar sua participação no prêmio BB."
              }
              btnText={"Continuar"}
              onClose={() => {
                navigate("/realizations/subscribe", {
                  state: { data: item, registration_id: subscribed._id },
                });
              }}
            />,
            "is-subscribed"
          );
        } else {
          navigate("/realizations/subscribe", { state: { data: item } });
        }
      }
    } catch (e) {
      navigate("/realizations/subscribe", { state: { data: item } });
      // console.log('next', e);
    }
  };

  return (
    <div>
      {firstLoaded && isElegible ? (
        <>
          <StepNav currentStep={1} activeSubStep={0} />
          <StepTitle
            title={
              "Selecione abaixo qual das suas realizações de 2024 com selo destaque você deseja inscrever no prêmio BB:"
            }
            description={
              "Lembre-se: a realização deve ter 2 ou mais participantes para concorrer ao Prêmio BB 2024. Caso a realização já tenha sido inscrita, confirme sua participação no prêmio BB."
            }
          />
          <NavTab
            itens={tabs.map((item) => {
              return {
                title: `${item.title}(${item.number})`,
                filter: item.filter,
                number: item.number,
              };
            })}
            selectedItem={currentTab}
            onChange={(selectItem: any) => setCurrentTab(selectItem)}
          />
          <Filter
            enableCategoryFilter={true}
            categories={categories}
            onChange={(key, value) =>
              setFilter({ ...filter, ...{ [key]: value } })
            }
          />
          {loading ? (
            <div className="flex items-start justify-center w-full h-screen">
              <div className="flex flex-col">
                <Spinner />
                <span className="text-center text-blue-bb">Carregando...</span>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 mx-4 md:mx-0 md:grid-cols-2 gap-7 content">
              {isLoaded && (
                <>
                  {currentTab.filter === "SUBSCRIPTION" &&
                  realizations.length == 0 ? (
                    <h4>Sem realizações disponíveis</h4>
                  ) : (
                    <></>
                  )}
                  {currentTab.filter === "CONFIRM" &&
                  subscribedRealization.length == 0 ? (
                    <h4>Sem realizações disponíveis</h4>
                  ) : (
                    <></>
                  )}
                  {(currentTab.filter == "SUBSCRIPTION"
                    ? realizations
                    : subscribedRealization
                  ).map(
                    (
                      item: Realization | RealizationSubscribed,
                      index: number
                    ) => (
                      <Card
                        data={item}
                        onNext={() => {
                          if (cardType == "SUBSCRIPTION") {
                            handlerNext(item);
                          } else {
                            handlerConfirm(item as RealizationSubscribed);
                          }
                        }}
                        type={cardType}
                        key={item._id}
                      />
                    )
                  )}
                </>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex items-start justify-center w-full h-screen">
            <div className="flex flex-col">
              <Spinner />
              <span className="text-center text-blue-bb">Aguarde...</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Realizations;
